import { compose, lifecycle, withHandlers, withProps, withState } from 'recompose';
import { connect } from 'react-redux';

import { t } from '../../../../modules/infra/i18n';

import withOwnerTypeAndId from '../../../common/hoc/withOwnerTypeAndId';
import { updateInvoiceStatus } from '../actions';
import { getOwnerTypeAndId, getShopNamesById } from '../../../auth/selectors';
import withNotifications from '../../../common/withNotifications';
import { withPaginatedStoreHelpers } from '../../../common/paginatedReduxStore';
import createPaginatedReduxStore from '../../../common/paginatedReduxStore/paginatedReduxStore';
import InvoiceAction from './InvoiceAction';
import { withPaymentInfoSetupStatus } from '../../../common/hoc/paymentInfoSetupStatus';

export const INVOICES_LIST_PAGE_SIZE = 20;

const storeControls = createPaginatedReduxStore({
  namespace: 'membershipInvoices',
  buildUrl: (state) => {
    const owner = getOwnerTypeAndId(state);
    return `/${owner.type}/${owner.id}/memberships/invoices`;
  },
  limit: INVOICES_LIST_PAGE_SIZE,
  httpVersion: 1,
});

const InvoicesListContainer = compose(
  withOwnerTypeAndId,
  connect(state => ({
    shopNamesById: getShopNamesById(state),
  }), null),
  withProps((props) => {
    const customFetchUrl = props.subscriptionId
      ? `/${props.ownerType}/${props.ownerId}/memberships/subscriptions/${props.subscriptionId}/invoices`
      : null;
    return {
      listFetchConfig: customFetchUrl ? { url: customFetchUrl } : {},
      showShopColumn: props.ownerType === 'brand' && !props.subscriptionId,
    };
  }),
  withNotifications,
  withPaginatedStoreHelpers(storeControls),
  withPaymentInfoSetupStatus,
  lifecycle({
    componentDidMount() {
      this.props.updateFetchConfig(this.props.listFetchConfig);
      this.props.openPage(1);
    },
  }),
  withState('activeStatus', 'setActiveStatus', 'all'),
  withState('pendingInvoices', 'setPendingInvoices', {}),
  withState('activeInvoice', 'setActiveInvoice', null),
  withState('selectedAction', 'setSelectedAction', null),
  withHandlers({
    handleStatusChange: props => (event) => {
      const status = event.target.value;
      const filters = status === 'all' ? {} : { status__in: status };
      props.updateFetchConfig({ ...props.listFetchConfig, filters });
      props.openPage(1);
      props.setActiveStatus(status);
    },
    performAction: props => async (invoice, action, payload = {}) => {
      props.setPendingInvoices({
        ...props.pendingInvoices,
        [invoice.id]: true,
      });

      const subscriptionId = props.subscriptionId || invoice.membershipSubscriptionId;

      try {
        await updateInvoiceStatus(props.ownerType, props.ownerId, subscriptionId, invoice.id, action, payload);
        await props.refreshPageData();
      } finally {
        props.setPendingInvoices({
          ...props.pendingInvoices,
          [invoice.id]: false,
        });
      }

      props.addNotification({
        title: t('common:success_title'),
        level: 'success',
        message: t('memberships:notifications.invoice_updated_success_message'),
      });
    },
    setAction: props => (invoice, action) => {
      props.setActiveInvoice(invoice);
      props.setSelectedAction(action);
    },
    clearSelectedAction: props => () => {
      props.setSelectedAction(null);
      props.setActiveInvoice(null);
    },
  }),
  withHandlers({
    modifyPaymentMethod: props => (paymentCard) => {
      props.performAction(props.activeInvoice, InvoiceAction.modifyPaymentMethod, { paymentCardId: paymentCard.id });
      props.clearSelectedAction();
    },
    changeDueDate: props => (dueDate) => {
      props.performAction(props.activeInvoice, InvoiceAction.changeDueDate, { dueDate });
      props.clearSelectedAction();
    },
  }),
);

export default InvoicesListContainer;
